"use client";

import Link from "next/link";
import Image from "next/image";
import { X } from "lucide-react";
import IconButton from "@/components/ui/icon-button";
import Button from "@/components/ui/button";
import Price from "@/components/ui/price";
import { cn } from "@/lib/cn";
import { products } from "@/lib/data";

type Props = {
  open: boolean;
  onClose: () => void;
};

// Visual-only mini-cart for Phase 2 — shows a static set of lines so the
// slide-over design can be reviewed. Shopify cart wiring comes later.
const demoLines = products.slice(0, 2).map((p, idx) => ({
  product: p,
  quantity: idx === 0 ? 2 : 1,
}));

const subtotal = demoLines.reduce(
  (acc, l) => acc + Number(l.product.priceRange.minVariantPrice.amount) * l.quantity,
  0,
);

export default function MiniCart({ open, onClose }: Props) {
  return (
    <>
      <div
        aria-hidden={!open}
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-40 bg-[color:var(--color-secondary)]/40 backdrop-blur-[2px] transition-opacity",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />
      <aside
        aria-hidden={!open}
        aria-label="Basket preview"
        className={cn(
          "fixed inset-y-0 right-0 z-50 w-full max-w-[24rem] bg-[color:var(--color-bg)] shadow-lift transition-transform",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        <header className="flex h-16 items-center justify-between border-b border-[color:var(--color-border)] px-4">
          <h2 className="text-lg font-semibold" style={{ fontFamily: "var(--font-serif)" }}>
            Your basket
          </h2>
          <IconButton label="Close basket" onClick={onClose}>
            <X size={20} />
          </IconButton>
        </header>
        <div className="flex h-[calc(100%-8rem)] flex-col overflow-y-auto divide-y divide-[color:var(--color-border)]">
          {demoLines.map(({ product, quantity }) => (
            <article key={product.id} className="flex gap-3 p-4">
              <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-[var(--radius-sm)] bg-[color:var(--color-surface-alt)]">
                <Image
                  src={product.featuredImage.url}
                  alt={product.featuredImage.altText ?? product.title}
                  fill
                  sizes="80px"
                  className="object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <Link
                  href={`/products/${product.handle}`}
                  onClick={onClose}
                  className="text-sm font-medium line-clamp-2 hover:text-[color:var(--color-primary)]"
                >
                  {product.title}
                </Link>
                <div className="mt-1 text-xs text-[color:var(--color-muted)]">
                  Qty {quantity}
                </div>
                <div className="mt-1">
                  <Price amount={product.priceRange.minVariantPrice} size="sm" />
                </div>
              </div>
            </article>
          ))}
        </div>
        <footer className="border-t border-[color:var(--color-border)] p-4 space-y-3 bg-[color:var(--color-surface)]">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[color:var(--color-muted)]">Subtotal</span>
            <Price
              amount={{ amount: subtotal.toFixed(2), currencyCode: "GBP" }}
              size="md"
            />
          </div>
          <Button href="/cart" variant="primary" fullWidth onClick={onClose}>
            View basket
          </Button>
        </footer>
      </aside>
    </>
  );
}
